import {
	type HeadersFunction,
	json,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useRouteError,
} from '@remix-run/react'
import './styles/tailwind.css'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import WhatsAppLogo from '#app/images/WhatsApp.png'
import AgeGate from './components/ageGate'
import Footer from './components/footer'
import Header from './components/header'
import Navbar from './components/navbar'
import { Paragraph } from './components/typography'
import { SessionStorageProvider } from './context/CartContext'
import { ClientHintCheck } from './utils/client-hints'
import { getSession } from './utils/client.server'
import { getEnv } from './utils/env.server'
import { honeypot } from './utils/honeypot.server'
import { useNonce } from './utils/nonce-provider'

export async function loader({ request }) {
	const session = await getSession(request.headers.get('Cookie'))
	const ageCheckCookie = session.get('ageGate')

	const honeyProps = honeypot.getInputProps()

	const data = {
		ENV: getEnv(),
		honeyProps,
		showAgeGate: ageCheckCookie,
	}

	console.log('Cookie: ', ageCheckCookie)
	console.log('Session: ', session)

	return json(data)
}

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		// { rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
		},
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Merriweather:ital,wght@0,400;0,700;1,400;1,700&display=swap',
		},
		// {
		// 	rel: 'manifest',
		// 	href: '/site.webmanifest',
		// 	crossOrigin: 'use-credentials',
		// } as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		// { rel: 'stylesheet', href: fontStylestylesheetUrl },
		// { rel: 'stylesheet', href: winewithmargaretStylesheetUrl },
		// { rel: 'stylesheet', href: tailwindStylesheetUrl },
		// cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null,
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = () => {
	return [
		{ title: 'Wine with Margaret' },
		{ name: 'description', content: "@winewithmargaret's website" },
	]
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

export function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()

	const showAgeGate = data.showAgeGate ? 'false' : 'true'
	return (
		<html lang="en" className="h-full overflow-x-hidden">
			{showAgeGate === 'true' ? <AgeGate /> : null}
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Links />
			</head>
			<body className="grid grid-cols-1 grid-rows-[1fr_3fr_1fr] lg:grid-rows-[1fr_4fr_1fr]">
				<SessionStorageProvider>
					<Header />
					<main className="row-start-2 mx-auto w-full">
						<Navbar />
						<Link
							to="https://wa.me/31655130880?text=Vraag%20vanaf%20winewithmargaret.com"
							className="fixed bottom-0 right-0 z-50 flex h-[60px] w-[45px] -translate-y-4 items-center justify-center rounded-l-md border-2 border-green-200 bg-white px-1 pl-2 pr-1 shadow-md shadow-green-500/50 transition-colors duration-150 hover:cursor-pointer hover:bg-green-50 lg:h-[100px] lg:w-[65px]"
						>
							<img
								src={WhatsAppLogo}
								alt="WhatsApp logo, click to chat with winewithmargaret"
							/>
						</Link>
						<Outlet />
					</main>
					<Footer />
					<script
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: `window.ENV = ${JSON.stringify(data.ENV)}`,
						}}
					/>
					<ScrollRestoration nonce={nonce} />
					<Scripts nonce={nonce} />
				</SessionStorageProvider>
				<div id="agegate-portal" style={{ zIndex: 1000 }} />
			</body>
		</html>
	)
}

export default function AppWithProvider() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	console.error(error)
	return (
		<html>
			<head>
				<title>Oh no! | Wine With Margaret</title>
				<Meta />
				<Links />
			</head>
			<body>
				<div className="flex h-screen flex-col items-center justify-center">
					<Paragraph>Er is iets mis gegaan...</Paragraph>
					<a className="text-blue-500 hover:underline">Probeer opnieuw</a>
				</div>
				<Scripts />
			</body>
		</html>
	)
}
